import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import apiClient from "@/api/client";
import { uploadManagedFile } from "@/utils/managedFile";
import type { ScanStatus } from "@/components/ui/ManagedFileLink";
import type { LeadStatus } from "@/features/leads/api/leads";

/**
 * Skrzynka zakupy@ — odpowiedzi firm.
 *
 * Odpowiedź NIGDY nie wychodzi sama: `useSendReply` woła się wyłącznie
 * z kliknięcia człowieka, a backend odmawia drugiej wysyłki na tę samą wiadomość.
 */

export type InboundKind =
  | "interested"
  | "not_interested"
  | "later"
  | "unsubscribe"
  | "question"
  | "bounce"
  | "other"
  | "";

export type InboundStatus = "new" | "handled" | "ignored";

/** Plik dołożony do odpowiedzi. Przed wysyłką da się go usunąć, po wysyłce
 *  zostaje jako zapis tego, co dostała firma. */
export interface ReplyAttachment {
  id: string;
  original_filename: string;
  size_bytes: number | null;
  scan_status?: ScanStatus;
  url: string | null;
  created_at: string;
}

/**
 * Plik, który PRZYSZEDŁ od firmy.
 *
 * `url` puste to nie błąd: plik od obcego nadawcy wychodzi dopiero po skanie
 * antywirusowym (`scan_status`). `download_error` mówi, że pliku nie ma u nas
 * w ogóle — wtedy zostaje Outlook.
 */
export interface InboundAttachment {
  id: string;
  name: string;
  content_type: string;
  size_bytes: number | null;
  /** Wklejony w treść (logo z podpisu) — nie jest dokumentem, nie pokazujemy go. */
  is_inline: boolean;
  url: string | null;
  scan_status?: ScanStatus;
  download_error: string;
}

export interface InboundMessage {
  id: string;
  lead: string | null;
  lead_name: string | null;
  /** Status firmy — po nim ekran wie, czy werdykt o niej już zapadł. "" bez firmy. */
  lead_status: LeadStatus | "";
  lead_status_label: string;
  /** Kampania, na której mail firma odpisała (po dopasowanym odbiorcy). */
  campaign: string | null;
  campaign_name: string | null;
  /** Kampanie, w których ta firma stoi zatrzymana — do cofnięcia jednym kliknięciem. */
  stopped_sequences: string[];
  from_email: string;
  from_name: string;
  subject: string;
  body: string;
  /** Oryginalny HTML wiadomości — pokazywać WYŁĄCZNIE po DOMPurify. */
  body_html: string;
  received_at: string;
  kind: InboundKind;
  kind_label: string;
  classification_note: string;
  /** „Nie teraz": za ile dni wrócić do firmy. Puste = obowiązuje wartość domyślna. */
  remind_in_days: number | null;
  proposed_reply: string;
  /**
   * Treść, która realnie wyszła do firmy. Pusta przy wątkach sprzed wprowadzenia
   * tego pola, których nie dało się odtworzyć z historii kontaktu.
   */
  reply_body: string;
  reply_attachments: ReplyAttachment[];
  /** Załączniki wiadomości od firmy — do pobrania z systemu. */
  attachments: InboundAttachment[];
  has_attachments: boolean;
  reply_sent_at: string | null;
  reply_sent_by_name: string | null;
  status: InboundStatus;
  status_label: string;
  handled_at: string | null;
  handled_by_name: string | null;
  actions: string[];
  error: string;
  classification_cost_usd: string;
}

const KEY = ["leads", "inbox"];

export function useInbox(params?: {
  all?: boolean;
  /** Rozpoznanie wiadomości — jedna wartość albo kilka po przecinku. */
  kind?: string;
  q?: string;
  /** Tylko odpowiedzi na maile tej kampanii (uuid). */
  campaign?: string;
}) {
  const query: Record<string, string> = {};
  if (params?.all) query.all = "1";
  if (params?.kind) query.kind = params.kind;
  if (params?.q) query.q = params.q;
  if (params?.campaign) query.campaign = params.campaign;

  return useQuery({
    queryKey: [...KEY, query],
    queryFn: () =>
      apiClient
        .get<{ data: InboundMessage[] }>("/leads/inbox/", { params: query })
        .then((r) => r.data.data),
    // Poczta przychodzi w tle co 5 minut; bez odświeżania user patrzy na
    // nieaktualną listę i nie wie, że ktoś czeka.
    refetchInterval: 60000,
    // Zmiana filtra (kampania, obsłużone) podmienia listę w miejscu — bez tego
    // cały ekran znikał pod spinnerem na czas pobrania.
    placeholderData: keepPreviousData,
  });
}

function useInboxMutation<TArgs, TResult = unknown>(
  fn: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });
}

export function useSaveProposal() {
  return useInboxMutation(({ id, text }: { id: string; text: string }) =>
    apiClient
      .patch<{ data: InboundMessage }>(`/leads/inbox/${id}/`, {
        proposed_reply: text,
      })
      .then((r) => r.data.data),
  );
}

/* DW/UDW dopisane ręcznie w oknie odpowiedzi. Puste tablice lecą razem z treścią —
   backend traktuje ich brak i pustkę tak samo, więc nie ma tu dwóch ścieżek. */
export function useSendReply() {
  return useInboxMutation(
    ({
      id,
      text,
      cc = [],
      bcc = [],
    }: {
      id: string;
      text: string;
      cc?: string[];
      bcc?: string[];
    }) =>
      apiClient
        .post<{ data: InboundMessage }>(`/leads/inbox/${id}/reply/`, {
          text,
          cc,
          bcc,
        })
        .then((r) => r.data.data),
  );
}

/**
 * Załącznik odpowiedzi — dwustopniowo jak pliki kampanii: najpierw generyczny
 * upload, potem podpięcie do wiadomości. Backend odbija to, czego Outlook
 * odbiorcy i tak by nie przepuścił (rozszerzenie, rozmiar, liczba plików).
 */
export function useUploadReplyAttachment() {
  return useInboxMutation(async ({ id, file }: { id: string; file: File }) => {
    const uploaded = await uploadManagedFile(file);
    return apiClient
      .post<{ data: ReplyAttachment }>(`/leads/inbox/${id}/attachments/`, {
        managed_file: uploaded.id,
      })
      .then((r) => r.data.data);
  });
}

export function useDeleteReplyAttachment() {
  return useInboxMutation((id: string) =>
    apiClient.delete(`/leads/inbox-attachments/${id}/`),
  );
}

export function useMarkHandled() {
  return useInboxMutation((id: string) =>
    apiClient
      .post<{ data: InboundMessage }>(`/leads/inbox/${id}/handled/`, {})
      .then((r) => r.data.data),
  );
}

/**
 * Cofa zatrzymanie sekwencji dla całej firmy.
 *
 * Autoresponder („jestem na urlopie") przychodzi z adresu firmy i zatrzymuje
 * przypomnienia dokładnie tak jak prawdziwa odpowiedź — rozpoznaje to człowiek.
 */
export function useResumeSequence() {
  return useInboxMutation((id: string) =>
    apiClient
      .post<{ data: InboundMessage }>(`/leads/inbox/${id}/resume-sequence/`, {})
      .then((r) => r.data.data),
  );
}

/**
 * Termin powrotu do firmy, która odpisała „nie teraz" — ustawiany osobno dla
 * każdej odpowiedzi. Przestawia zadanie założone przy odpowiedzi, nie dokłada
 * drugiego.
 */
export function useSetReminder() {
  return useInboxMutation(({ id, days }: { id: string; days: number }) =>
    apiClient
      .post<{ data: InboundMessage }>(`/leads/inbox/${id}/remind/`, { days })
      .then((r) => r.data.data),
  );
}

/**
 * Ręczne przypisanie wiadomości do firmy.
 *
 * Dopasowanie automatyczne idzie po adresie nadawcy i po naszym tokenie z cytatu.
 * Gdy człowiek odpisze z prywatnej skrzynki i skasuje cytat, nie ma czego złapać —
 * i wtedy tylko ta droga zatrzymuje przypomnienia do firmy, która nam odpisała.
 */
export function useAssignLead() {
  return useInboxMutation(({ id, lead }: { id: string; lead: string }) =>
    apiClient
      .post<{ data: InboundMessage }>(`/leads/inbox/${id}/assign-lead/`, { lead })
      .then((r) => r.data.data),
  );
}

/** „Nie kontaktować" wprost z czytanej wiadomości — z firmą albo sam adres. */
export function useBlockSender() {
  return useInboxMutation(({ id, reason }: { id: string; reason?: string }) =>
    apiClient
      .post<{ data: InboundMessage }>(`/leads/inbox/${id}/block/`, reason ? { reason } : {})
      .then((r) => r.data.data),
  );
}

/**
 * „Nie chcemy z nimi współpracować" — nasz werdykt, nie ich odmowa.
 *
 * Osobna droga od `useBlockSender`: tamto jest sprzeciwem odbiorcy i idzie na
 * globalną listę „nie kontaktować", to zostaje przy firmie jako decyzja handlowa.
 * Skutek dla kampanii jest ten sam — sekwencje stają natychmiast.
 */
export function useDeclineLead() {
  return useInboxMutation(({ id, reason }: { id: string; reason: string }) =>
    apiClient
      .post<{ data: InboundMessage }>(`/leads/inbox/${id}/decline-lead/`, { reason })
      .then((r) => r.data.data),
  );
}

export function useReclassify() {
  return useInboxMutation(({ id, kind }: { id: string; kind?: string }) =>
    apiClient
      .post<{ data: InboundMessage }>(`/leads/inbox/${id}/reclassify/`,
        kind ? { kind } : {},
      )
      .then((r) => r.data.data),
  );
}

export const INBOUND_KIND_COLORS: Record<string, "gray" | "blue" | "green" | "red" | "yellow"> = {
  interested: "green",
  question: "blue",
  later: "yellow",
  not_interested: "gray",
  unsubscribe: "red",
  bounce: "gray",
  other: "gray",
  "": "gray",
};

export const INBOUND_KINDS: { value: string; label: string }[] = [
  { value: "interested", label: "Zainteresowani" },
  { value: "question", label: "Pytanie" },
  { value: "later", label: "Nie teraz" },
  { value: "not_interested", label: "Nie są zainteresowani" },
  { value: "unsubscribe", label: "Proszą o niepisanie" },
  { value: "bounce", label: "Odbicie / autoodpowiedź" },
  { value: "other", label: "Inne" },
];
